import { useCallback, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { ModalShell, ModalInputField } from "../components/ModalShell";
import { MonthYearPicker } from "../components/MonthYearPicker";
import { FileUpload } from "../components/FileUpload";
import {
  ProposeChangeNotice,
  ReviewLockNotice,
} from "../components/ReviewLockNotice";
import { canRequestChange, reviewedFields } from "../utils/credentialEdit";
import {
  createCredential,
  getCredential,
  updateCredential,
} from "../api/credentials";
import { createDownloadUrl } from "../api/storage";
import {
  fetchOrganisationBranches,
  searchOrganisations,
} from "../api/organisations";
import {
  branchLabel,
  useOrganisationBranches,
} from "../hooks/useOrganisationBranches";
import { reviewLockNotice } from "../utils/credentialLock";

const EMPLOYMENT_TYPES = [
  "Full time",
  "Part time",
  "Contract",
  "Internship",
  "Freelance",
];

export function AddWorkPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    issuer: "",
    department: "",
    employmentType: "",
    location: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    description: "",
    ctc: "",
    reportingManagerName: "",
    reportingManagerDesignation: "",
  });
  const [certificate, setCertificate] = useState(null);
  const [status, setStatus] = useState(null);
  // The branch saved on the credential being edited, until it is selected
  // again (see the effect after the branch hook).
  const [savedBranch, setSavedBranch] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeable, setChangeable] = useState(false);
  const isVerified = status === "VERIFIED";
  const isView =
    location.pathname.includes("/view/") || searchParams.get("view") === "1";
  const isViewOnly = isView;
  const proposing =
    changeable && status === "VERIFIED" && searchParams.get("change") === "1";
  const locked =
    isViewOnly || (Boolean(reviewLockNotice(status)) && !proposing);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const c = await getCredential(id);
        setForm({
          title: c.title ?? "",
          issuer: c.issuer ?? "",
          department: c.metadata?.department ?? "",
          employmentType: c.metadata?.employmentType ?? "",
          location: c.metadata?.location ?? "",
          startDate: c.startDate ? c.startDate.slice(0, 7) : "",
          endDate: c.endDate ? c.endDate.slice(0, 7) : "",
          isCurrent: c.isCurrent ?? false,
          description: c.description ?? "",
          ctc: c.metadata?.ctc != null ? String(c.metadata.ctc) : "",
          reportingManagerName: c.metadata?.reportingManagerName ?? "",
          reportingManagerDesignation:
            c.metadata?.reportingManagerDesignation ?? "",
        });
        setStatus(c.status);
        setChangeable(canRequestChange(c));
        if (c.metadata?.branchId) {
          setSavedBranch({
            branchId: c.metadata.branchId,
            organisationId: c.metadata.branchOrganisationId,
          });
        }
        if (c.certificateS3Key) {
          setCertificate({
            s3Key: c.certificateS3Key,
            fileName: c.certificateFileName,
          });
        }
      } catch (err) {
        setError(err.message ?? "Failed to load credential");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleClose() {
    navigate("/profile");
  }

  // ---- Employer -> branch selection -------------------------------------
  //
  // The employer stays free text: a professional may well have worked for a
  // company that is not on TickAndPass. When what they typed DOES resolve to a
  // registered organisation, we offer that organisation's branches as a proper
  // dropdown instead of loose text.
  //
  // `matchedOrg` is the parent organisation. When it changes (different
  // employer typed) the hook clears any branch already picked, so a branch from
  // the previous employer can never be submitted against the new one.
  const [matchedOrg, setMatchedOrg] = useState(null);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    const issuer = form.issuer.trim();
    if (issuer.length < 2) {
      setMatchedOrg(null);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const orgs = await searchOrganisations(issuer, 3);
        const match = (Array.isArray(orgs) ? orgs : []).find(
          (o) => o.name?.toLowerCase() === issuer.toLowerCase(),
        );
        if (!cancelled) setMatchedOrg(match ?? null);
      } catch {
        if (!cancelled) setMatchedOrg(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form.issuer]);

  const fetchBranches = useCallback((id) => fetchOrganisationBranches(id), []);
  const { branches: branchOptions } = useOrganisationBranches(
    matchedOrg?.id ?? null,
    fetchBranches,
    branchId,
    setBranchId,
  );

  // Editing: the hook clears the branch whenever the employer's match
  // changes, so the saved branch is selected again once its organisation's
  // branches have loaded. Otherwise saving would drop it.
  useEffect(() => {
    if (
      savedBranch &&
      matchedOrg?.id === savedBranch.organisationId &&
      branchOptions.some((b) => b.id === savedBranch.branchId)
    ) {
      setBranchId(savedBranch.branchId);
      setSavedBranch(null);
    }
  }, [savedBranch, matchedOrg, branchOptions]);

  /** Picking a branch fills the location from that branch's own address. */
  function handleBranchChange(nextBranchId) {
    setBranchId(nextBranchId);
    const branch = branchOptions.find((b) => b.id === nextBranchId);
    if (branch) {
      update(
        "location",
        [branch.city, branch.state].filter(Boolean).join(", "),
      );
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (isViewOnly) return;
    if (locked) return save({ description: form.description.trim() });

    if (!form.title.trim()) return setError("Enter the designation.");
    if (!form.department.trim()) return setError("Enter the department.");
    if (!form.issuer.trim()) return setError("Enter the company / employer.");
    if (!form.employmentType) return setError("Pick an employment type.");
    if (!form.location.trim()) return setError("Enter the location.");
    if (!form.startDate) return setError("Pick the start month and year.");
    if (!form.isCurrent && !form.endDate) {
      return setError(
        'Pick the end month and year, or tick "I currently work here".',
      );
    }
    if (form.ctc && (!/^\d+$/.test(form.ctc) || Number(form.ctc) <= 0)) {
      return setError("Annual CTC must be a positive number in rupees.");
    }

    const startDate = `${form.startDate}-01`;
    const endDate =
      form.endDate && !form.isCurrent ? `${form.endDate}-01` : undefined;

    return save({
      type: "WORK",
      title: form.title.trim(),
      issuer: form.issuer.trim(),
      startDate,
      endDate,
      isCurrent: form.isCurrent,
      description: form.description.trim(),
      metadata: {
        department: form.department.trim(),
        employmentType: form.employmentType,
        location: form.location.trim(),
        // Durable link to the branch, alongside the human-readable location.
        // Only ever set when the branch came from the matched organisation's
        // own list, so it cannot point at another organisation's branch.
        ...(branchId && matchedOrg
          ? { branchId, branchOrganisationId: matchedOrg.id }
          : {}),
        ...(form.ctc ? { ctc: Number(form.ctc) } : {}),
        ...(form.reportingManagerName?.trim()
          ? { reportingManagerName: form.reportingManagerName.trim() }
          : {}),
        ...(form.reportingManagerDesignation?.trim()
          ? {
              reportingManagerDesignation:
                form.reportingManagerDesignation.trim(),
            }
          : {}),
      },
      certificateS3Key: certificate?.s3Key ?? null,
      certificateFileName: certificate?.fileName ?? null,
    });
  }

  /** Adds the credential, or saves the changes to the one being edited. */
  async function save(payload) {
    if (proposing) {
      navigate("/checkout", {
        state: {
          type: "VERIFICATION_FEE",
          credentialId: id,
          editChanges: reviewedFields(payload),
        },
      });
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCredential(id, payload);
      } else {
        await createCredential(payload);
      }

      handleClose();
    } catch (err) {
      setError(err.message ?? "Failed to save");
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <ModalShell
        title={
          isViewOnly
            ? "View Work Experience"
            : proposing
              ? "Change verified details"
              : isEdit && isVerified
                ? "Edit Additional Info"
                : isEdit
                  ? "Edit Work Experience"
                  : "Add Work Experience"
        }
        subtitle={
          isViewOnly
            ? "Work experience & filled details (view-only)"
            : proposing
              ? "Request a change to verified details (verification fee applies)"
              : isEdit && isVerified
                ? "Update additional info (verified details are locked)"
                : "Add a job, internship, or contract role for verification"
        }
        onClose={handleClose}
        maxWidth="max-w-xl"
      >
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Loading work experience details...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            {/* Form Fields Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-4 flex-1">
              {proposing ? (
                <ProposeChangeNotice onCancel={() => setSearchParams({})} />
              ) : (
                <ReviewLockNotice
                  status={status}
                  onRequestChange={
                    status === "VERIFIED" && changeable
                      ? () => setSearchParams({ change: "1" })
                      : undefined
                  }
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Designation / Job Title"
                  placeholder="e.g. Software Engineer"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Department"
                  placeholder="e.g. Engineering"
                  value={form.department}
                  onChange={(e) => update("department", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Company / Employer"
                  placeholder="e.g. Infosys"
                  value={form.issuer}
                  onChange={(e) => update("issuer", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Employment Type"
                  as="select"
                  placeholder="Select..."
                  options={EMPLOYMENT_TYPES}
                  value={form.employmentType}
                  onChange={(e) => update("employmentType", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              {/* Branch picker -- only when the typed employer resolves to a
                  registered organisation. Lists that organisation's ACTIVE
                  branches only, and resets when the employer changes. */}
              {matchedOrg && branchOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch at {matchedOrg.name}
                  </label>
                  <select
                    value={branchId}
                    onChange={(e) => handleBranchChange(e.target.value)}
                    disabled={locked}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-600 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">Select a branch (optional)</option>
                    {branchOptions.map((b) => (
                      <option key={b.id} value={b.id}>
                        {branchLabel(b)}
                      </option>
                    ))}
                  </select>
                  {!locked && (
                    <p className="mt-1 text-xs text-gray-500">
                      Picking a branch fills the location below.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Location"
                  placeholder="e.g. Bengaluru, India"
                  value={form.location}
                  onChange={(e) => {
                    // Typing a location by hand detaches it from the branch.
                    if (branchId) setBranchId("");
                    update("location", e.target.value);
                  }}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Annual CTC in rupees (optional)"
                  placeholder="e.g. 1200000"
                  inputMode="numeric"
                  value={form.ctc}
                  onChange={(e) =>
                    update("ctc", e.target.value.replace(/\D/g, ""))
                  }
                  disabled={locked}
                  hint="Seen only by the verifying employer, never on your shared profile"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MonthYearPicker
                  label="Start Month & Year"
                  value={form.startDate}
                  onChange={(v) => update("startDate", v)}
                  disabled={locked}
                  required
                />
                <MonthYearPicker
                  label={form.isCurrent ? "End (current)" : "End Month & Year"}
                  value={form.endDate}
                  onChange={(v) => update("endDate", v)}
                  disabled={locked || form.isCurrent}
                  required={!form.isCurrent}
                />
              </div>

              <label
                className={`flex items-center gap-2 text-xs font-medium text-slate-700 select-none ${
                  locked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.isCurrent}
                  onChange={(e) => {
                    update("isCurrent", e.target.checked);
                    if (e.target.checked) update("endDate", "");
                  }}
                  disabled={locked}
                  className="w-4 h-4 rounded border-slate-300 text-[#2563eb] focus:ring-[#2563eb]/20"
                />
                <span>I currently work here</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Reporting Manager Name (optional)"
                  placeholder="e.g. Rajesh Kumar"
                  value={form.reportingManagerName}
                  onChange={(e) =>
                    update("reportingManagerName", e.target.value)
                  }
                  disabled={locked}
                />
                <ModalInputField
                  label="Reporting Manager Designation (optional)"
                  placeholder="e.g. Engineering Manager"
                  value={form.reportingManagerDesignation}
                  onChange={(e) =>
                    update("reportingManagerDesignation", e.target.value)
                  }
                  disabled={locked}
                />
              </div>

              <ModalInputField
                label="Additional Info (optional)"
                placeholder="Describe responsibilities, achievements, key technologies..."
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                disabled={isViewOnly}
              />

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  Experience Letter / Offer Letter{" "}
                  <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <FileUpload
                  category="certificate"
                  value={certificate}
                  onUpload={setCertificate}
                  onClear={() => setCertificate(null)}
                  disabled={locked}
                />
                {!locked && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Upload your experience, offer, or relieving letter if
                    available (PDF, PNG, JPG &le; 5 MB).
                  </p>
                )}
                {certificate?.s3Key && (
                  <div className="mt-2.5">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await createDownloadUrl(
                            certificate.s3Key,
                          );
                          const url = res?.downloadUrl || res?.url || res;
                          if (url) {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } else {
                            throw new Error("Unable to retrieve file URL.");
                          }
                        } catch (err) {
                          setError(err.message || "Failed to open document.");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs font-semibold transition-colors shadow-xs cursor-pointer"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View Uploaded Document</span>
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {isViewOnly ? (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md border ${
                    isVerified
                      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : "text-slate-700 bg-slate-100 border-slate-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${isVerified ? "bg-emerald-600" : "bg-slate-400"}`}
                  />
                  {isVerified ? "Verified • View-only" : "View-only"}
                </span>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/credentials/edit/work/${id}`)}
                    className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer"
                  >
                    {isVerified ? "Edit Info" : "Edit Details"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <div>
                  {isEdit && (
                    <button
                      type="button"
                      onClick={() => navigate(`/credentials/view/work/${id}`)}
                      className="px-3.5 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View details</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 transition-colors shadow-sm cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 cursor-pointer"
                  >
                    {submitting
                      ? "Saving..."
                      : proposing
                        ? "Continue to Fee"
                        : isEdit && isVerified
                          ? "Save Additional Info"
                          : isEdit
                            ? "Save changes"
                            : "Add work experience"}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </ModalShell>
    </AppShell>
  );
}
